import { EditOutlined, FileUploadOutlined } from "@mui/icons-material";
import { Tooltip } from "@mui/material";
import { grey } from "@mui/material/colors";
import { ModuleRegistry } from "ag-grid-community";
import {
  RowGroupingModule,
  RowGroupingPanelModule,
  TreeDataModule,
} from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { isEmpty, isFunction, isNil } from "lodash";
import { useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import { useColumnTypes } from "../../_src_shared/utils/agGrid/columnTypes/hooks/useColumnTypes";
import { sidebarOpen } from "../../redux/reducers/sidebarReducer";
import AgGridTableWrapper from "../agGrid/AgGridTableWrapper";
import Flx from "../layout/Flx";
import { DASHBOARD_SIDEBAR_TYPES } from "../sidebars/DashboardSidebar/DashboardSidebar";
import Txt from "../typography/Txt";
import { useFileDocGroupAndTypesHook } from "../../_src_shared/hooks/useFileDocGroupAndTypesHook";

ModuleRegistry.registerModules([
  RowGroupingPanelModule,
  RowGroupingModule,
  TreeDataModule,
]);

const MissingFilesTable = ({
  files,
  docGroup,
  docType,
  height = "100%",
  emptyContent,
  emptyMessage,
  simpleTable = true,
  gridOptions,
  headerHeight = 0,
  quickFilterText,
  showFileOptions = false,
  showUploadDate = false,
  rowGroup = null,
  hideFileCellDate = false,
  showStatus = false,
  rowStyles,
  hideDocType = false,
  defaultView = false,
  hideDocGroup = false,
  getRowStyle,
  sx,
}) => {
  const dispatch = useDispatch();
  const { formatDocTypeLabel } = useFileDocGroupAndTypesHook({});
  const columnDefs = useMemo(
    () => [
      {
        headerName: "Type",
        valueGetter: (params) => {
          const value = params.data?.docType;
          return value === undefined || value === null || value === ""
            ? "Uncategorized"
            : value;
        },
        valueFormatter: (params) => {
          const value = params.data?.docType;
          return formatDocTypeLabel(value);
        },

        rowGroup: rowGroup === "docType",
        minWidth: 180,
        flex: 1,
        sort: rowGroup === "docType" ? "asc" : null,
        field: "docType",
        hide:
          rowGroup === "docType"
            ? true
            : isNil(docType) && !hideDocType
              ? false
              : true,
        comparator: (valueA, valueB) => {
          if (valueA === "Uncategorized" && valueB !== "Uncategorized")
            return 1;
          if (valueA !== "Uncategorized" && valueB === "Uncategorized")
            return -1;
          return valueA.localeCompare(valueB);
        },
      },
      {
        headerName: "Category",
        valueGetter: (params) => {
          const value = params.data?.docGroup;
          return value === undefined || value === null || value === ""
            ? "Uncategorized"
            : value;
        },
        rowGroup: rowGroup === "docGroup",
        sort: rowGroup === "docGroup" ? "asc" : null,
        field: "docGroup",
        hide:
          rowGroup === "docGroup"
            ? true
            : isNil(docGroup) && !hideDocGroup
              ? false
              : true,
        comparator: (valueA, valueB) => {
          if (valueA === "Uncategorized" && valueB !== "Uncategorized")
            return 1;
          if (valueA !== "Uncategorized" && valueB === "Uncategorized")
            return -1;
          return valueA.localeCompare(valueB);
        },
      },

      {
        width: 38,
        cellRenderer: CellRendererFileOptions,
        hide: !showFileOptions,
        field: "actions",
        headerName: "",
        pinned: "right",
        cellStyle: {
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          display: "flex",
          cursor: "pointer",
          paddingLeft: 0,
          paddingRight: 0,
        },
      },
    ],
    [
      docGroup,
      docType,
      rowGroup,
      hideDocType,
      hideDocGroup,
      showFileOptions,
      formatDocTypeLabel,
    ],
  );
  const columnTypes = useColumnTypes();

  const getRowId = useCallback((params) => {
    return params.data._id;
  }, []);

  const defaultColDef = useMemo(() => {
    return {
      cellClass: "ag-cell-vertical-center",
    };
  }, []);

  const onCellClicked = useCallback(
    (params) => {
      if (params?.data?.isMissing) {
        dispatch(
          sidebarOpen(DASHBOARD_SIDEBAR_TYPES.ADD_DOCUMENT, params.data, {
            ...params?.data,
            title: `Upload ${params.data.docType || "Document"}`,
          }),
        );
        return;
      }
    },
    [dispatch],
  );

  const onRowClicked = useCallback((params) => {
    const clickedElement = params.event?.target;
    // console.log("params.event?.target", params);
    if (
      (clickedElement instanceof Element && clickedElement.closest("button")) ||
      (clickedElement instanceof Element && clickedElement.closest("a"))
    ) {
      return;
    }
    if (params.node.group) {
      params.node.setExpanded(!params.node.expanded);
      return;
    }
  }, []);

  const styles = useMemo(() => {
    return {
      position: "relative",
      flexGrow: 1,
      p: 0,
      overflow: "hidden",
      ...sx,
    };
  }, [sx]);

  const getRowStyles = useCallback(
    (params) => {
      if (isFunction(getRowStyle)) {
        return getRowStyle(params);
      }
      const renderedRowStyles = isFunction(rowStyles) ? rowStyles(params) : {};
      if (params.node.group) {
        return {
          fontWeight: "600",
          backgroundColor: grey[50],
          cursor: "pointer",
          borderBottom: `1px solid ${grey[200]}`,
          // ...renderedRowStyles,
        };
      }
      return { ...renderedRowStyles };
    },
    [getRowStyle, rowStyles],
  );

  if ((emptyMessage || emptyContent) && isEmpty(files)) {
    if (emptyContent) {
      return emptyContent;
    }
    return (
      <Txt secondary sx={{ p: 1 }}>
        {emptyMessage}
      </Txt>
    );
  }

  return (
    <>
      <AgGridTableWrapper
        height={height}
        suppressMinHeightWhenPopulated={files}
        simpleTable={simpleTable}
        sx={styles}
      >
        <AgGridReact
          rowData={files}
          columnDefs={columnDefs}
          // onFirstDataRendered={onFirstDataRendered}
          defaultColDef={defaultColDef}
          quickFilterText={quickFilterText}
          onCellClicked={onCellClicked}
          onRowClicked={onRowClicked}
          headerHeight={headerHeight}
          columnTypes={columnTypes}
          getRowId={getRowId}
          getRowStyle={getRowStyles}
          groupDisplayType={"groupRows"}
          groupDefaultExpanded={1}
          domLayout="autoHeight"
          tooltipShowDelay={100}
          enableCellTextSelection
          tooltipMouseTrack
          gridOptions={gridOptions}
        />
      </AgGridTableWrapper>
    </>
  );
};

const CellRendererFileOptions = (params) => {
  const file = useMemo(() => params?.data, [params?.data]);

  let content = null;
  if (params?.data?.isMissing) {
    content = (
      <Tooltip title={`Upload ${file.docType}`}>
        <FileUploadOutlined className="thin" />
      </Tooltip>
    );
  } else {
    content = (
      <Tooltip title="Edit File">
        <EditOutlined className="thin" />
        {/* <IconButton
          onClick={() =>
            dispatch(
              sidebarOpen(
                DASHBOARD_SIDEBAR_TYPES.EDIT_BORROWER_DOCUMENT_METADATA,
                file,
              ),
            )
          }
          size="small"
        >
          <EditOutlined className="thin" />
        </IconButton> */}
      </Tooltip>
    );
  }

  return (
    <Flx sx={{ height: "100%" }} center>
      {content}
    </Flx>
  );
};

export default MissingFilesTable;
